import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d0d7de",
    },
    "&:hover fieldset": {
      borderColor: "#7338AC",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#7338AC",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#7338AC",
  },
};

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().required("Description is required"),
  skill_level: yup.number().required("Skill Level is required"),
  category: yup.string().required("Category is required"),
});

const DialogAddCourse = (props) => {
  const { open, onClose, onSuccess, mode = "add", dataToEdit } = props;
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      skill_level: "",
      category: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  // Fetch categories for dropdown
  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = () => {
    axiosInstance
      .get(ApiEndPoints.CATEGORIES.list)
      .then((response) => {
        setCategories(response.data?.data?.categories || []);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });
  };

  useEffect(() => {
    if (open && dataToEdit && mode === "edit") {
      reset({
        title: dataToEdit.title || "",
        description: dataToEdit.description || "",
        skill_level: dataToEdit.skill_level || "",
        category: dataToEdit.category || "",
      });
    } else if (open && mode === "add") {
      reset({
        title: "",
        description: "",
        skill_level: "",
        category: "",
      });
    }
  }, [dataToEdit, open, mode, reset]);

  const onSubmit = (data) => {
    setLoading(true);

    const payload = {
      title: data.title,
      description: data.description,
      skill_level: Number(data.skill_level),
      category: data.category, // Keep as string (UUID)
    };

    if (mode === "add") {
      axiosInstance
        .post(ApiEndPoints.COURSES.create, payload)
        .then((response) => {
          toastSuccess(response.data?.message || "Course added successfully");
          onSuccess();
          onClose();
        })
        .catch((error) => {
          toastError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Edit mode - include is_active for updates
      const editPayload = {
        ...payload,
      };

      axiosInstance
        .patch(ApiEndPoints.COURSES.update(dataToEdit.id), editPayload)
        .then((response) => {
          toastSuccess(response.data?.message || "Course updated successfully");
          onSuccess();
          onClose();
        })
        .catch((error) => {
          toastError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {mode === "add" ? "Add Course" : "Edit Course"}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: "grey.500",
            "&:hover": {
              backgroundColor: "grey.100",
            },
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" sx={{ mt: 1 }}>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Title"
                error={!!errors.title}
                helperText={errors.title?.message}
                sx={textFieldStyles}
                margin="normal"
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                label="Description"
                error={!!errors.description}
                helperText={errors.description?.message}
                sx={textFieldStyles}
                margin="normal"
              />
            )}
          />

          <Controller
            name="skill_level"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.skill_level}
              >
                <InputLabel>Skill Level</InputLabel>
                <Select
                  {...field}
                  label="Skill Level"
                  sx={textFieldStyles}
                >
                  <MenuItem value={1}>Beginner</MenuItem>
                  <MenuItem value={2}>Intermediate</MenuItem>
                  <MenuItem value={3}>Advanced</MenuItem>
                </Select>
                {errors.skill_level && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.skill_level.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.category}
              >
                <InputLabel>Category</InputLabel>
                <Select
                  {...field}
                  label="Category"
                  sx={textFieldStyles}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.category.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#d0d7de",
            color: "#656d76",
            "&:hover": {
              borderColor: "#7338AC",
              color: "#7338AC",
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          variant="contained"
        >
          {mode === "add" ? "Add Course" : "Update Course"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DialogAddCourse;
